import type { MetricStatus, ScoreGrade } from '@/types/gmb';

// 11 項健康度指標定義與配分 (滿分合計 100)。
// V1 Conversion-1.1｜已同步 Backend calculate_gmb_audit_score 正式封版規格
// （METRIC_MAX / METRIC_LABEL / resolveHealthGrade）。名稱與滿分一律以 Backend 為準，
// 前端不重算分數，只作為公開方法說明與顯示層的單一來源。
// 這是評分「規則」而非店家資料，因此可安全放在前端。

export interface MetricDefinition {
  code: string;
  name: string;
  maxScore: number;
  description: string;
}

export const METRIC_DEFINITIONS: MetricDefinition[] = [
  { code: 'M01', name: '營業狀態', maxScore: 6, description: '確認 Google 公開資料中的營業狀態是否正常。' },
  { code: 'M02', name: '主要商家類別', maxScore: 8, description: '確認商家的主要類別是否清楚，能否合理辨識主要服務性質。' },
  { code: 'M03', name: '地址／服務區設定', maxScore: 8, description: '確認實體商家地址或服務區域商家的公開設定是否完整。' },
  { code: 'M04', name: '聯絡電話', maxScore: 6, description: '確認是否提供可供顧客聯絡的公開電話。' },
  { code: 'M05', name: '官方網站', maxScore: 8, description: '確認是否提供可正常導向的官方網站連結。' },
  { code: 'M06', name: '營業時間', maxScore: 8, description: '確認是否提供公開且可辨識的營業時間資訊。' },
  { code: 'M07', name: '商家照片', maxScore: 6, description: '確認 Google 公開資料中是否有可取得的商家照片資料。' },
  { code: 'M08', name: '評分絕對值', maxScore: 12, description: '依目前 Google 公開評分進行基礎評估。' },
  { code: 'M09', name: '評論數絕對值', maxScore: 10, description: '依目前 Google 公開評論數量進行基礎評估。' },
  { code: 'M10', name: '評分與同業比較', maxScore: 12, description: '比較本商家評分與本次選取之附近同業樣本中位數的相對表現。' },
  { code: 'M11', name: '評論數與同業比較', maxScore: 16, description: '比較本商家評論數與本次選取之附近同業樣本中位數的相對表現。' },
];

export const TOTAL_MAX_SCORE = METRIC_DEFINITIONS.reduce((sum, m) => sum + m.maxScore, 0);

export const METRIC_DEFINITION_MAP: Record<string, MetricDefinition> = METRIC_DEFINITIONS.reduce(
  (acc, m) => {
    acc[m.code] = m;
    return acc;
  },
  {} as Record<string, MetricDefinition>,
);

export const METRIC_STATUS_LABEL: Record<MetricStatus, string> = {
  pass: '通過',
  warn: '待改善',
  fail: '不合格',
  na: '資料不足',
};

export const METRIC_STATUS_STYLE: Record<MetricStatus, string> = {
  pass: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  warn: 'bg-amber-50 text-amber-700 border-amber-200',
  fail: 'bg-rose-50 text-rose-700 border-rose-200',
  na: 'bg-slate-100 text-slate-600 border-slate-200',
};

export const METRIC_STATUS_DOT: Record<MetricStatus, string> = {
  pass: 'bg-emerald-500',
  warn: 'bg-amber-500',
  fail: 'bg-rose-500',
  na: 'bg-slate-400',
};

export const CONFIDENCE_LABEL: Record<string, string> = {
  High: '可信度高',
  Medium: '可信度中',
  Low: '可信度低',
};

interface GradeThreshold {
  grade: ScoreGrade;
  min: number;
  max: number;
  label: string;
}

/**
 * V1 正式 Grade 規格（與 Backend resolveHealthGrade 完全一致）。
 * A 90–100 優良 / B 80–89 良好 / C 70–79 尚可 / D 60–69 待加強 / E 0–59 優先改善。
 */
export const GRADE_THRESHOLDS: GradeThreshold[] = [
  { grade: 'A', min: 90, max: 100, label: '優良' },
  { grade: 'B', min: 80, max: 89, label: '良好' },
  { grade: 'C', min: 70, max: 79, label: '尚可' },
  { grade: 'D', min: 60, max: 69, label: '待加強' },
  { grade: 'E', min: 0, max: 59, label: '優先改善' },
];

export function resolveGrade(total: number): ScoreGrade {
  const found = GRADE_THRESHOLDS.find((t) => total >= t.min);
  return found ? found.grade : 'E';
}

export function gradeLabel(grade: ScoreGrade): string {
  const found = GRADE_THRESHOLDS.find((t) => t.grade === grade);
  return found ? found.label : '';
}

/** 顯示用範圍文字（例如 90–100）。 */
export function gradeRangeText(threshold: GradeThreshold): string {
  return `${threshold.min}–${threshold.max}`;
}

/**
 * 同業樣本可信度（SampleConfidence）顯示規格。
 * 這是「同業樣本可信度」，不是整份報告的絕對可信度。
 */
export interface PeerSampleConfidenceDef {
  key: string;
  label: string;
  sampleRange: string;
  description: string;
}

export const PEER_SAMPLE_CONFIDENCE: PeerSampleConfidenceDef[] = [
  { key: 'HIGH', label: '高', sampleRange: '7–10 家有效同業樣本', description: '本次取得較充足的同業樣本，相對比較參考程度較高。' },
  { key: 'MEDIUM', label: '中', sampleRange: '4–6 家有效同業樣本', description: '本次取得基本可用的同業樣本，相對比較具基本參考性。' },
  { key: 'LOW', label: '低', sampleRange: '1–3 家有效同業樣本', description: '本次同業樣本偏少，相對比較僅供初步參考。' },
  { key: 'NONE', label: '同業資料不足', sampleRange: '0 家', description: '本次未取得可比較的同業樣本，相關同業比較指標不納入有效評分。' },
];